/**
 * QA Tab Component for Admin Panel
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { LogIn, Bug, Loader2, X, Coins } from 'lucide-react';

export const QATab = ({
  userRole,
  customers,
  customersLoading,
  bugs,
  bugsLoading,
  bugsStats,
  bugStatusFilter,
  setBugStatusFilter,
  selectedBug,
  setSelectedBug,
  startImpersonation,
  qaAddCredits,
  fetchBugs,
  updateBugStatus
}) => {
  return (
    <>
      {/* Bug Stats for Admin */}
      {userRole === 'admin' && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Open', value: bugsStats.open || 0, color: 'border-red-500', textColor: 'text-red-700' },
            { label: 'In Progress', value: bugsStats.in_progress || 0, color: 'border-blue-500', textColor: 'text-blue-700' },
            { label: 'Resolved', value: bugsStats.resolved || 0, color: 'border-green-500', textColor: 'text-green-700' },
            { label: 'Critical', value: bugsStats.critical || 0, color: 'border-orange-500', textColor: 'text-orange-700' },
            { label: 'Total', value: bugsStats.total || 0, color: 'border-zinc-500', textColor: 'text-zinc-700' }
          ].map((stat, i) => (
            <Card key={i} className={`border-l-4 ${stat.color} bg-white`}>
              <CardContent className="p-3">
                <p className="text-zinc-500 text-xs">{stat.label}</p>
                <p className={`text-xl font-bold ${stat.textColor}`}>{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Impersonation */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <LogIn className="w-4 h-4" /> Test as Customer
            </CardTitle>
          </CardHeader>
          <CardContent>
            {customersLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {customers.map(customer => (
                  <div 
                    key={customer.user_id}
                    className="p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-sm">{customer.name || customer.email}</p>
                        <p className="text-xs text-zinc-500">{customer.email}</p>
                        <p className="text-xs text-zinc-400">{customer.workspace_name || 'No workspace'} • <span className="font-medium text-amber-600">{customer.credits || 0} credits</span></p>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-orange-500 hover:bg-orange-600"
                        onClick={() => startImpersonation(customer)}
                        data-testid={`impersonate-${customer.user_id}`}
                      >
                        <LogIn className="w-4 h-4 mr-1" /> Test
                      </Button>
                    </div>
                    {/* Add Credits Section */}
                    <div className="flex items-center gap-2 pt-2 border-t border-zinc-100">
                      <Input
                        type="number"
                        min="1"
                        max="10000"
                        placeholder="100"
                        className="w-24 h-8 text-sm"
                        defaultValue={100}
                        id={`credits-${customer.user_id}`}
                        data-testid={`credits-input-${customer.user_id}`}
                      />
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-green-200 text-green-700 hover:bg-green-50"
                        onClick={() => {
                          const input = document.getElementById(`credits-${customer.user_id}`);
                          const credits = parseInt(input?.value) || 100;
                          qaAddCredits(customer.user_id, credits);
                        }}
                        data-testid={`add-credits-${customer.user_id}`}
                      >
                        <Coins className="w-4 h-4 mr-1" /> Add Credits
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bug Tickets (Admin Only) */}
        {userRole === 'admin' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bug className="w-4 h-4" /> Bug Reports
                </CardTitle>
                <select 
                  className="text-xs border rounded px-2 py-1"
                  value={bugStatusFilter}
                  onChange={(e) => { setBugStatusFilter(e.target.value); fetchBugs(); }}
                >
                  <option value="all">All</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
            </CardHeader>
            <CardContent>
              {bugsLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : bugs.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Bug className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
                  <p className="text-sm">No bug reports yet</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {bugs.map(bug => (
                    <div 
                      key={bug.ticket_id}
                      className={`p-3 rounded-lg border cursor-pointer transition ${selectedBug?.ticket_id === bug.ticket_id ? 'border-amber-300 bg-amber-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
                      onClick={() => setSelectedBug(bug)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              bug.priority === 'critical' ? 'bg-red-100 text-red-700' :
                              bug.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                              bug.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-zinc-100 text-zinc-700'
                            }`}>{bug.priority}</span>
                            <span className="text-xs text-zinc-400">{bug.ticket_id}</span>
                          </div>
                          <p className="font-medium text-sm mt-1">{bug.title}</p>
                          <p className="text-xs text-zinc-500 mt-1">
                            By {bug.reported_by_email} • {bug.role_tested} role
                          </p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          bug.status === 'open' ? 'bg-red-100 text-red-700' :
                          bug.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }`}>{bug.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bug Detail Panel */}
      {selectedBug && userRole === 'admin' && (
        <BugDetailPanel 
          selectedBug={selectedBug}
          setSelectedBug={setSelectedBug}
          updateBugStatus={updateBugStatus}
        />
      )}
    </>
  );
};

// Bug Detail Panel Sub-component
const BugDetailPanel = ({ selectedBug, setSelectedBug, updateBugStatus }) => (
  <Card className="mt-6">
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="text-base">{selectedBug.ticket_id}: {selectedBug.title}</CardTitle>
        <Button variant="ghost" size="sm" onClick={() => setSelectedBug(null)}>
          <X className="w-4 h-4" />
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-xs text-zinc-500">Status</p>
              <select 
                className="border rounded px-2 py-1 text-sm"
                value={selectedBug.status}
                onChange={(e) => updateBugStatus(selectedBug.ticket_id, e.target.value)}
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
                <option value="wont_fix">Won&apos;t Fix</option>
              </select>
            </div>
            <div>
              <p className="text-xs text-zinc-500">Priority</p>
              <span className={`px-2 py-1 rounded text-sm ${
                selectedBug.priority === 'critical' ? 'bg-red-100 text-red-700' :
                selectedBug.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                'bg-amber-100 text-amber-700'
              }`}>{selectedBug.priority}</span>
            </div>
            <div>
              <p className="text-xs text-zinc-500">Role Tested</p>
              <span className="text-sm">{selectedBug.role_tested}</span>
            </div>
          </div>
          <div>
            <p className="text-xs text-zinc-500 mb-1">Description</p>
            <p className="text-sm bg-zinc-50 p-3 rounded">{selectedBug.description || 'No description'}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500 mb-1">Steps to Reproduce</p>
            <p className="text-sm bg-zinc-50 p-3 rounded whitespace-pre-wrap">{selectedBug.steps_to_reproduce || 'Not provided'}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-zinc-500 mb-1">Expected</p>
              <p className="text-sm bg-zinc-50 p-3 rounded">{selectedBug.expected_behavior || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 mb-1">Actual</p>
              <p className="text-sm bg-zinc-50 p-3 rounded">{selectedBug.actual_behavior || '-'}</p>
            </div>
          </div>
          <div className="text-xs text-zinc-500">
            <p>Page: {selectedBug.page_url}</p>
            <p>Testing as: {selectedBug.testing_as_email}</p>
            <p>Reported: {new Date(selectedBug.created_at).toLocaleString()}</p>
          </div>
        </div>
        <div>
          <p className="text-xs text-zinc-500 mb-2">Screenshot</p>
          {selectedBug.screenshot_base64 ? (
            <img src={selectedBug.screenshot_base64} alt="Bug screenshot" className="w-full rounded border" />
          ) : (
            <div className="bg-zinc-100 rounded p-8 text-center text-zinc-400">No screenshot</div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

export default QATab;
